import { useMutation } from '@apollo/client';

import { apolloClientContracts } from '@/apollo/clients/contracts.apollo';
import {
  GetContractDetails,
  GetContractDetailsVariables,
} from '@/graphql/contracts/_generated_/getContractDetails.query';
import {
  ResumeBilling,
  ResumeBillingVariables
} from '@/graphql/contracts/_generated_/resumeBilling.mutation';
import GetContractDetailsQuery from '@/graphql/contracts/getContractDetails.query.gql';
import ResumeBillingMutation from '@/graphql/contracts/resumeBilling.mutation.gql';
import { notificationService } from '@/services/notification/notification.client';
import { ALL_ITEMS } from '@/config/common.config';
import GetBCContractLinesQuery from '@/graphql/contracts/getPaginatedFilteredBCContractLines.query.gql';

export type ResumeBillingParams = Omit<ResumeBillingVariables, 'contractId'>;

export default function useResumeBilling(contractId: UUID) {
  const [_resumeBilling, { loading, error }] = useMutation<ResumeBilling, ResumeBillingVariables>(
    ResumeBillingMutation,
    {
      client: apolloClientContracts,
      onError: (err) => notificationService.open({
        title: 'Error',
        text: err.message,
        type: 'danger'
      }),
      update: (_, { data }) => {
        if (!data) return;

        const cachedContract = apolloClientContracts.readQuery<GetContractDetails, GetContractDetailsVariables>({
          query: GetContractDetailsQuery,
          variables: { contractId }
        });

        const updatedContract: Contract = {
          ...cachedContract!.contract,
          billing: data.resumeBilling.billing
        };

        apolloClientContracts.writeQuery<GetContractDetails, GetContractDetailsVariables>({
          query: GetContractDetailsQuery,
          variables: { contractId },
          data: {
            contract: updatedContract
          }
        });
      },
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetBCContractLinesQuery,
          variables: {
            filter: {
              contractId: { eq: contractId }
            },
            first: ALL_ITEMS
          }
        }
      ],
      onCompleted: () => {
        notificationService.open({
          title: 'Resume billing',
          text: 'The contract billing has been successfully resumed',
          type: 'success'
        });
      }
    }
  );

  const resumeBilling = (inputParams: ResumeBillingParams) => {
    _resumeBilling({
      variables: {
        contractId,
        comment: inputParams.comment ?? null
      }
    });
  };

  return {
    resumeBilling,
    isResumeBillingLoading: loading,
    errorResumeBilling: error
  } as const;
}
